/// <reference types="vite/client" />

import { vueErrorHandler } from '@/global-error-handler'
import router from '@/router'
import { axiosClient } from '@/utils/axios-client'
import { LzpLibrary, type CameraElement } from '@logistiek-zonder-papier/lzp-library'
import * as Sentry from '@sentry/browser'
import { createPinia } from 'pinia'
import { type App, createApp, type DefineComponent } from 'vue'
import { LIBRARY_CONTEXT } from '@/main'
import { getPhoto } from '@/utils/camera.utils'

// This is glue to make it possible to have drop in vue components in the project
// This not in any way shape or form exemplary code.

// TODO phase out this code
const modules: Record<string, { default: DefineComponent }> = import.meta.glob('@/**/*.vue', {
  eager: true,
})

export const pinia = createPinia()
window.pinia = pinia

const components = Object.fromEntries(
  Object.entries(modules).map(([path, mod]) => {
    // Extract file name (e.g., "MyButton.vue" → "MyButton")
    const name = path.split('/').pop()!.replace('.vue', '')
    return [name, mod]
  })
)

const activeFloatingInstances: App<Element>[] = []

/**
 * Finds all elements in the page with the data-vue-component attribute and mounts the names component in place
 */
export function mountFloatingVueInstances() {
  const elements = document.querySelectorAll('[data-vue-component]') as NodeListOf<HTMLElement>
  for (const element of elements) {
    const rootComponentName = element.getAttribute('data-vue-component')
    if (rootComponentName == null || components[rootComponentName] == null) {
      Sentry.captureEvent({
        message: `Missing vue component, component ${rootComponentName} was not found`,
      })
      continue
    }
    const app = createApp(components[rootComponentName].default, getComponentProps(element))
    app.config.errorHandler = vueErrorHandler

    app.use(LzpLibrary, {
      request: axiosClient.request,
      getPhoto: async (config: CameraElement) => await getPhoto(config),
      context: LIBRARY_CONTEXT,
      environment: import.meta.env.MODE,
    })
    app.use(pinia)
    app.use(router)
    activeFloatingInstances.push(app)
    element.__vue = app.mount(element)
  }
}

function getComponentProps(element: HTMLElement): Partial<Record<string, string>> {
  const props = { ...element.dataset }
  delete props['vueComponent']
  return props
}

export function unmountFloatingVueInstances() {
  let app
  while ((app = activeFloatingInstances.pop()) != null) {
    app.unmount()
  }
}

// This is needed to make sure that the Vue components in the newly loaded page are properly mounted and rendered.
// Specifically for IDF details modal
window.addEventListener('reload-vue', () => {
  unmountFloatingVueInstances()
  mountFloatingVueInstances()
})
